import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

const PAYMENT_TERMS = [0, 7, 14, 30, 60, 90, 180]
const TYPES = ['Business','Individual','Restaurant','Supermarket','Hotel','Other']

export default function Customers() {
  const { user, isAdminOrManager } = useAuth()
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('list')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [selected, setSelected] = useState(null)
  const [search, setSearch] = useState('')

  const empty = { name:'', contact_person:'', phone:'', email:'', address:'',
    customer_type:'Restaurant', credit_limit:'', payment_terms:30,
    early_pay_discount:0, notes:'' }
  const [form, setForm] = useState(empty)

  useEffect(() => { loadCustomers() }, [])

  const loadCustomers = async () => {
    setLoading(true)
    const { data } = await supabase.from('customer_balances').select('*').order('name')
    setCustomers(data || [])
    setLoading(false)
  }

  const handleSave = async () => {
    if (!form.name) { setMsg({ type:'error', text:'Customer name is required.' }); return }
    setSaving(true)
    const payload = { ...form, credit_limit: parseFloat(form.credit_limit)||0, created_by: user.id }
    const { error } = selected
      ? await supabase.from('customers').update(payload).eq('id', selected.id)
      : await supabase.from('customers').insert(payload)
    if (error) { setMsg({ type:'error', text: error.message }); setSaving(false); return }
    setMsg({ type:'success', text: selected ? 'Customer updated.' : 'Customer added.' })
    setSaving(false); setTab('list'); setSelected(null); setForm(empty)
    loadCustomers()
    setTimeout(() => setMsg(null), 3000)
  }

  const handleEdit = (c) => {
    setSelected(c)
    setForm({ name:c.name, contact_person:c.contact_person||'', phone:c.phone||'',
      email:c.email||'', address:c.address||'', customer_type:c.customer_type||'Restaurant',
      credit_limit:c.credit_limit||'', payment_terms:c.payment_terms||30,
      early_pay_discount:c.early_pay_discount||0, notes:c.notes||'' })
    setTab('form')
  }

  const [holdModal, setHoldModal] = React.useState(null)
  const [holdReason, setHoldReason] = React.useState('')

  const handleHold = (c) => {
    if (c.is_on_hold) {
      // Remove hold immediately
      supabase.from('customers').update({ is_on_hold: false, hold_reason: null }).eq('id', c.id).then(() => loadCustomers())
    } else {
      setHoldModal(c); setHoldReason('')
    }
  }

  const handleConfirmHold = async () => {
    if (!holdReason.trim()) return
    await supabase.from('customers').update({ is_on_hold: true, hold_reason: holdReason }).eq('id', holdModal.id)
    setHoldModal(null); setHoldReason(''); loadCustomers()
  }

  const filtered = customers.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
  const totalReceivables = customers.reduce((s, c) => s + (c.outstanding_balance||0), 0)
  const overdue = customers.filter(c => c.days_oldest_unpaid > (c.payment_terms||0))
  const overLimit = customers.filter(c => c.over_credit_limit)

  return (
    <AppLayout title="Customers">
      <div className="section-header">
        <div className="section-icon">🤝</div>
        <div className="section-info">
          <h2>Customer Accounts</h2>
          <p>Manage customer credit limits, payment terms, and outstanding balances.</p>
        </div>
        {isAdminOrManager && (
          <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={() => { setSelected(null); setForm(empty); setTab('form') }}>
            + New Customer
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom:18 }}>
        <div className="kpi green"><div className="kpi-label">Total Customers</div><div className="kpi-value">{customers.length}</div></div>
        <div className="kpi"><div className="kpi-label">Total Receivables</div><div className="kpi-value" style={{ fontSize:'1.2rem' }}>UGX {totalReceivables.toLocaleString()}</div></div>
        <div className={`kpi ${overdue.length>0?'red':''}`}><div className="kpi-label">Overdue Accounts</div><div className="kpi-value">{overdue.length}</div><div className="kpi-note">{overdue.length===0?'All within terms':'Require attention'}</div></div>
        <div className={`kpi ${overLimit.length>0?'red':''}`}><div className="kpi-label">Over Credit Limit</div><div className="kpi-value">{overLimit.length}</div></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='list'?'active':''}`} onClick={() => setTab('list')}>👥 All Customers</button>
        <button className={`tab ${tab==='receivables'?'active':''}`} onClick={() => setTab('receivables')}>💰 Receivables</button>
        {isAdminOrManager && <button className={`tab ${tab==='form'?'active':''}`} onClick={() => { setSelected(null); setForm(empty); setTab('form') }}>+ {selected?'Edit':'New'}</button>}
      </div>

      {tab === 'list' && (
        <div className="card">
          <input className="form-input" placeholder="🔍 Search customers..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom:14, maxWidth:300 }} />
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : filtered.length === 0 ? <div className="empty-state"><div className="empty-icon">🤝</div><h3>No customers yet</h3><p>Add your first customer to get started.</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Customer</th><th>Type</th><th>Outstanding</th><th>Credit Limit</th><th>Credit Available</th><th>Terms</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} style={{ background: c.is_on_hold ? '#fff8f0' : c.over_credit_limit ? '#fff5f5' : undefined }}>
                      <td>
                        <div style={{ fontWeight:700 }}>{c.name}</div>
                        {c.contact_person && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{c.contact_person}</div>}
                        {c.phone && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontFamily:"'DM Mono',monospace" }}>{c.phone}</div>}
                      </td>
                      <td><span className="badge badge-grey">{c.customer_type}</span></td>
                      <td style={{ fontWeight:700, color: c.outstanding_balance>0?'var(--amber)':'var(--green-mid)' }}>
                        UGX {(c.outstanding_balance||0).toLocaleString()}
                      </td>
                      <td style={{ fontSize:'0.82rem' }}>{c.credit_limit>0 ? `UGX ${c.credit_limit.toLocaleString()}` : 'No limit'}</td>
                      <td>
                        {c.credit_limit > 0 ? (
                          <span style={{ color: c.over_credit_limit?'var(--red)':c.credit_available<c.credit_limit*0.2?'var(--amber)':'var(--green-mid)', fontWeight:600 }}>
                            UGX {(c.credit_available||0).toLocaleString()}
                          </span>
                        ) : <span style={{ color:'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontSize:'0.78rem' }}>{c.payment_terms===0?'On delivery':`Net ${c.payment_terms}`}</td>
                      <td>
                        {c.is_on_hold ? <span className="badge badge-red">⚠️ On Hold</span>
                        : c.over_credit_limit ? <span className="badge badge-red">Over Limit</span>
                        : c.days_oldest_unpaid > c.payment_terms ? <span className="badge badge-amber">Overdue</span>
                        : <span className="badge badge-green">Good Standing</span>}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5 }}>
                          {isAdminOrManager && <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(c)}>Edit</button>}
                          {isAdminOrManager && <button className={`btn btn-sm ${c.is_on_hold?'btn-secondary':'btn-danger'}`} onClick={() => handleHold(c)}>{c.is_on_hold?'Unhold':'Hold'}</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'receivables' && (
        <div className="card">
          <div className="card-title">Receivables — Money Owed to Kobflo</div>
          <div className="card-subtitle">All outstanding invoice balances by customer</div>
          {customers.filter(c => c.outstanding_balance > 0).length === 0 ? (
            <div className="empty-state"><div className="empty-icon">✅</div><h3>No outstanding receivables</h3><p>All customer balances are clear.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Customer</th><th>Open Invoices</th><th>Outstanding (UGX)</th><th>Oldest Unpaid</th><th>Days Overdue</th><th>Action</th></tr></thead>
                <tbody>
                  {customers.filter(c => c.outstanding_balance > 0).sort((a,b) => b.outstanding_balance - a.outstanding_balance).map(c => {
                    const daysOverdue = Math.max(0, (c.days_oldest_unpaid||0) - (c.payment_terms||0))
                    return (
                      <tr key={c.id} style={{ background: daysOverdue > 14 ? '#fff5f5' : daysOverdue > 0 ? '#fffbf0' : undefined }}>
                        <td style={{ fontWeight:700 }}>{c.name}</td>
                        <td>{c.open_invoices}</td>
                        <td style={{ fontWeight:700, color:'var(--amber)', fontSize:'1rem', fontFamily:"'Cormorant Garamond',serif" }}>
                          {(c.outstanding_balance||0).toLocaleString()}
                        </td>
                        <td style={{ fontSize:'0.78rem' }}>{c.oldest_unpaid_date ? new Date(c.oldest_unpaid_date).toLocaleDateString('en-UG') : '—'}</td>
                        <td>
                          {daysOverdue > 0
                            ? <span className={`badge ${daysOverdue>30?'badge-red':daysOverdue>14?'badge-amber':'badge-amber'}`}>{daysOverdue} days</span>
                            : <span className="badge badge-green">Within terms</span>}
                        </td>
                        <td>
                          {daysOverdue >= 14 && (
                            <span style={{ fontSize:'0.72rem', color:'var(--red)', fontWeight:700 }}>⚠️ Notify Edward & Sharon</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  <tr style={{ background:'var(--green-pale)' }}>
                    <td colSpan={2} style={{ fontWeight:700 }}>TOTAL RECEIVABLES</td>
                    <td style={{ fontWeight:700, fontSize:'1.1rem', fontFamily:"'Cormorant Garamond',serif", color:'var(--green-deep)' }}>
                      UGX {totalReceivables.toLocaleString()}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'form' && (
        <div className="card">
          <div className="card-title">{selected ? 'Edit Customer' : 'New Customer'}</div>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">Customer Name *</label><input className="form-input" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Café Javas" /></div>
            <div className="form-group"><label className="form-label">Customer Type</label>
              <select className="form-select" value={form.customer_type} onChange={e => setForm(f=>({...f,customer_type:e.target.value}))}>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={form.contact_person} onChange={e => setForm(f=>({...f,contact_person:e.target.value}))} placeholder="Name of purchasing manager" /></div>
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+256..." /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="orders@customer.com" /></div>
            <div className="form-group"><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e => setForm(f=>({...f,address:e.target.value}))} placeholder="Delivery address" /></div>
            <div className="form-group">
              <label className="form-label">Credit Limit (UGX) <span style={{ color:'var(--text-muted)', fontWeight:400 }}>— leave 0 for no limit</span></label>
              <input className="form-input" type="number" value={form.credit_limit} onChange={e => setForm(f=>({...f,credit_limit:e.target.value}))} placeholder="0" />
            </div>
            <div className="form-group"><label className="form-label">Payment Terms</label>
              <select className="form-select" value={form.payment_terms} onChange={e => setForm(f=>({...f,payment_terms:parseInt(e.target.value)}))}>
                {PAYMENT_TERMS.map(d => <option key={d} value={d}>{d===0?'On Delivery':`Net ${d} days`}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Early Payment Discount (%)</label><input className="form-input" type="number" step="0.5" value={form.early_pay_discount} onChange={e => setForm(f=>({...f,early_pay_discount:parseFloat(e.target.value)||0}))} placeholder="0" /></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder="Any special terms, delivery instructions, or notes..." /></div>
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner" />Saving...</>:'💾 Save Customer'}</button>
            <button className="btn btn-ghost" onClick={() => { setTab('list'); setSelected(null); setForm(empty) }}>Cancel</button>
          </div>
        </div>
      )}

      {holdModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setHoldModal(null)}>
          <div className="modal">
            <div className="modal-title">⚠️ Place Customer On Hold</div>
            <div className="modal-subtitle"><strong>{holdModal.name}</strong> — no new sales will be allowed while on hold.</div>
            <div className="form-group">
              <label className="form-label">Reason for Hold *</label>
              <textarea className="form-textarea" style={{ minHeight:80 }} value={holdReason} onChange={e=>setHoldReason(e.target.value)} placeholder="e.g. Overdue balance not paid for 60 days, disputed invoice..." autoFocus />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setHoldModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleConfirmHold} disabled={!holdReason.trim()}>⚠️ Confirm Hold</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
